import React, { useEffect, useCallback, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  getMarketingKpisData,
  getMarketingLeadsMensualesData,
  getMarketingPorFuenteData,
  getMarketingEmbudoData,
  getMarketingCostePorLeadData,
  getMarketingParcialData,
  getGscKpisData,
} from "../../slices/dashboardMarketing/thunk";

import { I } from "../../Components/CMI/icons";
import { KPI, CompactKPI, ChartCard, Badge, Legend, Alert, Empty } from "../../Components/CMI/primitives";
import { GroupedBarChart, DonutChart, DonutLegend, Funnel, fmtEur, fmtN, fmtPct } from "../../Components/CMI/charts";
import PageHead, { SectionHead, Footer } from "../../Layouts/CMI/PageHead";
import { MESES, mesesHastaActual, rangoMes } from "../../helpers/meses";

const MONTHS = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];
const MESES_OPCIONES = mesesHastaActual();

const deltaPill = (pct) => {
  if (pct == null || isNaN(pct)) return null;
  const pos = pct >= 0;
  return { text: `${pos ? "↑" : "↓"} ${Math.abs(pct).toFixed(1).replace(".", ",")}%`, tone: pos ? "pos" : "neg" };
};

const empresaLabel = (id) => {
  if (id === "all") return "Todas";
  if (id === 13) return "Visados";
  if (id === 12) return "Permiso";
  if (id === 14) return "CVS";
  if (id === "visados_cvs") return "Visados+CVS";
  return String(id);
};

const ParcialCard = ({ tone, icon, label, obj, fmt, info }) => {
  const actual = obj?.actual ?? 0;
  const anterior = obj?.anterior;
  const pct = obj?.variacion_pct;
  const pill = deltaPill(pct);
  return (
    <CompactKPI
      tone={tone} icon={icon} label={label}
      value={fmt ? fmt(actual) : actual}
      prev={anterior != null ? (fmt ? fmt(anterior) : anterior) : null}
      delta={pill?.text}
      deltaTone={pill?.tone}
      info={info}
    />
  );
};

const DashboardMarketing = () => {
  document.title = "Marketing | CMI - Grupo Cucalón Estévez";
  const dispatch = useDispatch();
  const { empresaId, year, vista } = useSelector((s) => s.Filters);
  const { kpis, leadsMensuales, porFuente, embudo, costePorLead, parcial, gsc } = useSelector((s) => s.DashboardMarketing);
  const [mes, setMes] = useState(new Date().getMonth());

  // Datos anuales (no dependen del mes)
  const loadAll = useCallback(() => {
    dispatch(getMarketingKpisData());
    dispatch(getMarketingLeadsMensualesData());
    dispatch(getMarketingPorFuenteData());
    dispatch(getMarketingEmbudoData());
    dispatch(getMarketingCostePorLeadData());
  }, [dispatch]);

  // Datos del mes (parciales + Search Console)
  const loadMensual = useCallback(() => {
    const filtro = rangoMes(new Date().getFullYear(), mes);
    dispatch(getMarketingParcialData(filtro));
    dispatch(getGscKpisData(filtro));
  }, [dispatch, mes]);

  useEffect(() => { loadAll(); }, [loadAll, empresaId, year]);
  useEffect(() => {
    if (vista !== "mensual") return;
    loadMensual();
  }, [loadMensual, empresaId, vista]);
  useEffect(() => {
    const h = () => { loadAll(); loadMensual(); };
    window.addEventListener("cmi:refresh", h);
    return () => window.removeEventListener("cmi:refresh", h);
  }, [loadAll, loadMensual]);

  const leadsAno = kpis?.leads_ano?.total ?? 0;
  const leadsAnoDelta = deltaPill(kpis?.leads_ano?.variacion_vs_ano_anterior);
  const leadsMes = kpis?.leads_mes_actual ?? 0;
  const clientesFirme = kpis?.clientes_en_firme_ano ?? 0;
  const tasaConv = kpis?.tasa_conversion ?? null;

  const meses = leadsMensuales?.meses || [];
  const leadsCur = MONTHS.map((_, i) => meses[i]?.leads_nuevos ?? 0);
  const leadsPrev = MONTHS.map((_, i) => meses[i]?.leads_ano_anterior ?? 0);
  const convertidos = MONTHS.map((_, i) => meses[i]?.convertidos ?? 0);

  const etapas = embudo?.etapas || [];
  const totalEntrados = etapas[0]?.total || 1;
  const funnelData = etapas.map((e) => ({
    label: e.etapa,
    value: e.total,
    pct: `${((e.total / totalEntrados) * 100).toFixed(1).replace(".", ",")}%`,
  }));
  const descartados = embudo?.descartados ?? 0;

  const fuenteColors = [1,3,6,4,7,2,5,8,9,10].map(n => `var(--ce-chart-${n})`);
  const fuentesArr = (porFuente?.fuentes || []).slice(0, 9).map((f, i) => ({
    label: f.fuente || "Sin fuente",
    value: f.total_leads || 0,
    color: fuenteColors[i % fuenteColors.length],
  }));
  const topFuente = fuentesArr[0];

  const canales = costePorLead?.canales || [];
  const cplTotales = costePorLead?.totales || {};

  const p = parcial?.kpis || {};

  const gscReady = !!gsc?.kpis && !gsc?.error;
  const gscError = gsc?.error;

  return (
    <>
      <PageHead
        icon={I.Megaphone}
        title="Dashboard Marketing"
        sub={vista === "anual"
          ? `Datos anuales · empresa ${empresaLabel(empresaId)} · año ${year}`
          : `Datos mensuales · empresa ${empresaLabel(empresaId)} · ${MESES[mes]}`}
        screenId="marketing"
      />

      {vista === "anual" && (
      <>
      <div className="grid gc-4">
        <KPI
          tier="hero" tone="primary" icon={I.UserPlus}
          label="Leads del Año"
          value={fmtN(leadsAno)}
          delta={leadsAnoDelta?.text} deltaTone={leadsAnoDelta?.tone} deltaRef="vs año anterior"
          info={{
            descripcion: "Total de leads (no falsos) creados durante el año seleccionado, con la variación interanual.",
            origen: "BD erpvisados_2023 — tabla leads.",
            formula: "COUNT(*) WHERE YEAR(created) = año AND (es_falso = 0 OR es_falso IS NULL).",
          }}
        />
        <KPI
          tier="primary" tone="cyan" icon={I.Calendar}
          label="Leads Este Mes"
          value={fmtN(leadsMes)}
          info={{
            descripcion: "Leads creados en el mes en curso (mes actual del calendario, dentro del año seleccionado).",
            origen: "BD erpvisados_2023 — tabla leads.",
            formula: "COUNT(*) WHERE YEAR(created) = año AND MONTH(created) = MONTH(CURDATE()).",
          }}
        />
        <KPI
          tier="primary" tone="success" icon={I.CheckCheck}
          label="Clientes en Firme"
          value={fmtN(clientesFirme)}
          extras={<span className="kpi-ref">Convertidos este año</span>}
          info={{
            descripcion: "Leads que pasaron al estado 'cliente en firme' durante el año (estadolead_id = 4).",
            origen: "BD erpvisados_2023 — tabla leads.",
            formula: "COUNT(*) WHERE estadolead_id = 4 AND YEAR(fecha_en_firme) = año.",
          }}
        />
        <KPI
          tier="primary" tone="primary" icon={I.Percent}
          label="Tasa de Conversión"
          value={tasaConv != null ? fmtPct(tasaConv) : "—"}
          extras={<span className="kpi-ref">Leads → Cliente firme</span>}
          info={{
            descripcion: "Porcentaje de leads del año que han llegado al estado 'cliente en firme'.",
            origen: "Calculado en API a partir de leads y clientes en firme (BD).",
            formula: "Clientes en firme del año / Leads del año × 100.",
          }}
        />
      </div>

      <div className="grid gc-8-4" style={{ marginTop: 20 }}>
        <ChartCard
          icon={I.ChartLine} title="Evolución de Leads"
          info={{
            titulo: "Evolución de Leads",
            descripcion: "Comparativa mensual de leads del año vs año anterior, con la línea de leads convertidos a cliente en firme.",
            origen: "BD erpvisados_2023 — tabla leads (agrupada por mes de created y de fecha_en_firme).",
            formula: "Leads = COUNT(*) GROUP BY MONTH(created). Convertidos = COUNT(*) WHERE estadolead_id = 4 GROUP BY MONTH(fecha_en_firme).",
          }}
          legend={<Legend items={[
            { color: "var(--ce-chart-1)", label: `Leads ${year}` },
            { color: "var(--ce-chart-previous)", label: `Leads ${year - 1}` },
            { color: "var(--ce-chart-3)", label: "Convertidos" },
          ]}/>}
        >
          {meses.length ? (
            <GroupedBarChart
              months={MONTHS}
              series={[
                { label: `Leads ${year}`, color: "var(--ce-chart-1)", values: leadsCur },
                { label: `Leads ${year - 1}`, color: "var(--ce-chart-previous)", values: leadsPrev, muted: true },
                { label: "Convertidos", color: "var(--ce-chart-3)", values: convertidos },
              ]}
            />
          ) : (
            <Empty icon={I.BarChart3} title="Sin datos de leads" />
          )}
        </ChartCard>

        <ChartCard
          icon={I.Funnel} title="Embudo de Conversión"
          info={{
            titulo: "Embudo de Conversión",
            descripcion: "Volumen de leads del año que han alcanzado cada etapa del embudo (Entrados, En Evaluación, Prospect, Cliente en Firme), más el número de descartados.",
            origen: "BD erpvisados_2023 — tabla leads (campos fecha_en_evaluacion, fecha_en_prospect, fecha_en_firme, fecha_descartado).",
            formula: "Cada etapa = COUNT(*) WHERE YEAR(created) = año AND fecha_<etapa> IS NOT NULL.",
          }}
          meta={descartados ? <Badge tone="warn">{fmtN(descartados)} descartados</Badge> : null}
        >
          {funnelData.length ? (
            <Funnel data={funnelData}/>
          ) : (
            <Empty icon={I.Funnel} title="Sin datos de embudo" />
          )}
        </ChartCard>
      </div>

      <div className="grid gc-2" style={{ marginTop: 20 }}>
        <ChartCard
          icon={I.PieChart} title="Leads por Fuente"
          info={{
            titulo: "Leads por Fuente",
            descripcion: "Top 10 fuentes de captación del año (formularios web, campañas, etc.) con el volumen de leads y la tasa de conversión a cliente en firme.",
            origen: "BD erpvisados_2023 — leads LEFT JOIN customersources.",
            formula: "COUNT(*) y SUM(estadolead_id = 4) GROUP BY customersource_id WHERE YEAR(created) = año. Tasa = convertidos / total_leads × 100.",
          }}
          foot={
            topFuente ? (
              <>
                <span>Top fuente · <strong style={{color:"var(--ce-fg-2)"}}>{topFuente.label}</strong></span>
                <span>{fmtN(topFuente.value)} leads</span>
              </>
            ) : null
          }
        >
          {fuentesArr.length ? (
            <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
              <DonutChart data={fuentesArr} totalLabel="Total leads" totalValueFmt={fmtN} size={180} />
              <DonutLegend data={fuentesArr.slice(0, 6)} totalForPct={fuentesArr.reduce((a,b)=>a+b.value,0)} />
            </div>
          ) : (
            <Empty icon={I.PieChart} title="Sin leads por fuente" />
          )}
        </ChartCard>

        <ChartCard
          icon={I.Tag} title="Coste por Lead (Marketing)"
          info={{
            titulo: "Coste por Lead (Marketing)",
            descripcion: "Cruce de la inversión en marketing pagada (Holded) con el volumen de leads de la fuente equivalente (BD), por canal.",
            origen: "API Holded — purchases con contactName que contiene marketing/google/linkedin/meta/facebook/tiktok/instagram. Leads: BD leads JOIN customersources.",
            formula: "Coste por lead = gasto_canal / leads_canal. Mapeo Holded→BD: Google → formularios web; LinkedIn → LinkedIn; Meta/Facebook → Facebook + Instagram.",
          }}
          meta={
            cplTotales.gasto_marketing != null ? (
              <>
                <strong style={{ color: "var(--ce-amber-ink)" }}>{fmtEur(cplTotales.gasto_marketing)}</strong>
                {cplTotales.coste_por_lead_medio != null && (
                  <span>{fmtEur(cplTotales.coste_por_lead_medio)} / lead</span>
                )}
              </>
            ) : null
          }
        >
          {canales.length ? (
            <table className="tbl">
              <thead>
                <tr>
                  <th>Canal</th>
                  <th className="right">Inversión</th>
                  <th className="right">Leads</th>
                  <th className="right">Coste / Lead</th>
                </tr>
              </thead>
              <tbody>
                {canales.map((c, i) => (
                  <tr key={i}>
                    <td>{c.canal}</td>
                    <td className="right num">{fmtEur(c.gasto_total)}</td>
                    <td className="right num">{fmtN(c.leads)}</td>
                    <td className="right amount">{c.coste_por_lead != null ? fmtEur(c.coste_por_lead) : "—"}</td>
                  </tr>
                ))}
                {cplTotales.gasto_marketing != null && (
                  <tr style={{ background: "var(--ce-bg-2)" }}>
                    <td><strong>Total</strong></td>
                    <td className="right amount">{fmtEur(cplTotales.gasto_marketing)}</td>
                    <td className="right num">{fmtN(cplTotales.leads_atribuidos)}</td>
                    <td className="right amount">{cplTotales.coste_por_lead_medio != null ? fmtEur(cplTotales.coste_por_lead_medio) : "—"}</td>
                  </tr>
                )}
              </tbody>
            </table>
          ) : (
            <Empty icon={I.Tag} title="Sin gasto en marketing" />
          )}
        </ChartCard>
      </div>

      </>
      )}

      {vista === "mensual" && (
      <>
      <SectionHead
        icon={I.Calendar} title="Datos parciales"
        sub="Comparativa contra el mes anterior"
        right={
          <select className="sel" value={mes} onChange={(e) => setMes(Number(e.target.value))} aria-label="Mes">
            {MESES_OPCIONES.map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
        }
      />
      <div className="grid gc-3">
        <ParcialCard tone="primary" icon={I.UserPlus}   label="Leads"             obj={p.leads}           fmt={fmtN}
          info={{
            descripcion: "Leads no falsos creados en el rango seleccionado.",
            origen: "BD erpvisados_2023 — tabla leads.",
            formula: "COUNT(*) WHERE DATE(created) BETWEEN from AND to.",
          }}
        />
        <ParcialCard tone="success" icon={I.CheckCheck} label="Clientes en firme" obj={p.clientes_firme}  fmt={fmtN}
          info={{
            descripcion: "Leads convertidos a cliente en firme dentro del rango.",
            origen: "BD erpvisados_2023 — tabla leads.",
            formula: "COUNT(*) WHERE DATE(fecha_en_firme) BETWEEN from AND to.",
          }}
        />
        <ParcialCard tone="cyan"    icon={I.Percent}    label="Tasa conversión"   obj={p.tasa_conversion} fmt={(v)=>fmtPct(v)}
          info={{
            descripcion: "Porcentaje de leads del rango que se han convertido a cliente en firme.",
            origen: "Calculado en API a partir de leads y clientes en firme (BD).",
            formula: "Clientes en firme del rango / Leads del rango × 100.",
          }}
        />
        <ParcialCard tone="warning" icon={I.Coin}       label="Gasto marketing"   obj={p.gasto_marketing} fmt={fmtEur}
          info={{
            descripcion: "Inversión en marketing pagada en el rango (Holded), filtrada por proveedores cuyo nombre contiene palabras clave de marketing.",
            origen: "API Holded — /invoicing/v1/documents/purchase.",
            formula: "Σ subtotal de purchases con contactName que contenga marketing/google/linkedin/meta/facebook/tiktok/instagram, dentro del rango.",
          }}
        />
        <ParcialCard tone="primary" icon={I.Tag}        label="Coste por lead"    obj={p.coste_por_lead}  fmt={fmtEur}
          info={{
            descripcion: "Cuánto cuesta de media cada lead en el rango.",
            origen: "Calculado en API a partir del gasto en marketing (Holded) y los leads (BD).",
            formula: "Gasto marketing del rango / Leads del rango.",
          }}
        />
        <ParcialCard tone="danger"  icon={I.Slash}      label="Descartados"       obj={p.descartados}     fmt={fmtN}
          info={{
            descripcion: "Leads marcados como descartados dentro del rango seleccionado.",
            origen: "BD erpvisados_2023 — tabla leads.",
            formula: "COUNT(*) WHERE DATE(fecha_descartado) BETWEEN from AND to.",
          }}
        />
      </div>

      <SectionHead icon={I.Search} title="Google Search Console" />
      {gscError ? (
        <Alert
          tone="warn" icon={I.Triangle}
          title="Propiedad de Search Console no configurada para esta empresa."
          sub="Configura el dominio en la sección de Configuración para empezar a recibir métricas de tráfico orgánico."
        />
      ) : gscReady ? (
        <Alert tone="ok" icon={I.Check}
          title={`Search Console activo · ${gsc.kpis?.totales?.clicks ?? 0} clicks · ${gsc.kpis?.totales?.impressions ?? 0} impresiones`}
        />
      ) : (
        <Alert tone="info" icon={I.Info} title="Cargando datos de Search Console…" />
      )}
      </>
      )}

      <Footer />
    </>
  );
};

export default DashboardMarketing;
